/**
 * Shared portal header — logo, title, nav actions (Help, Admin, Credits, Buy, Sign Out).
 * Replaces the inline header block that previously lived in every page component.
 *
 * The `showCredits` prop controls whether the credit badge + Buy Credits button render.
 * Admin-only pages (e.g. AdminDashboard) pass `showCredits={false}`.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAdmin } from '../../hooks/useAdmin';
import { useImpersonation } from '../../hooks/useImpersonation';

interface PortalHeaderProps {
  /** Show the credit badge + Buy Credits button. Defaults to true (client-facing pages). */
  showCredits?: boolean;
  /** Credit balance to display. Ignored if showCredits is false. */
  activeBalance?: number;
  /** Billing type — controls "Call" vs "Contact" label. */
  billingType?: 'per_call' | 'per_contact';
  /** Click handler for the Buy Credits button. Required if showCredits is true. */
  onBuyCreditsClick?: () => void;
  /**
   * Click handler for the blue "Buy New Leads" button (next to the title).
   * Only rendered when supplied AND not impersonating. Gated upstream by the
   * buy-leads rollout flag (see useBuyLeads) — pass undefined to hide.
   */
  onBuyLeadsClick?: () => void;
  /** True while the buy-leads order is being minted — disables the button. */
  buyLeadsBusy?: boolean;
}

export function PortalHeader({
  showCredits = true,
  activeBalance = 0,
  billingType = 'per_call',
  onBuyCreditsClick,
  onBuyLeadsClick,
  buyLeadsBusy = false,
}: PortalHeaderProps) {
  const { signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const { isImpersonating } = useImpersonation();

  return (
    <header className="header">
      <div className="header-left">
        <img src="/logo.png" alt="JAD Enterprises" className="header-logo" />
        <h1>AI Calling Portal</h1>
        {onBuyLeadsClick && !isImpersonating && (
          <button
            onClick={onBuyLeadsClick}
            disabled={buyLeadsBusy}
            className="btn btn-blue"
            style={{ padding: '6px 14px', fontSize: '0.8rem', marginLeft: '12px',
                     opacity: buyLeadsBusy ? 0.7 : 1 }}
          >
            {buyLeadsBusy ? 'Opening…' : 'Buy New Leads'}
          </button>
        )}
      </div>
      <div className="header-right">
        <Link to="/help" className="btn btn-secondary" aria-label="Help Center">
          Help
        </Link>
        {isAdmin && !isImpersonating && (
          <Link
            to="/admin"
            className="btn btn-secondary"
            style={{ borderColor: 'var(--gold-dark)' }}
          >
            Admin Panel
          </Link>
        )}
        {showCredits && (
          <div
            className="credit-badge"
            style={activeBalance < 0 ? { background: '#dc2626', color: '#fff' } : {}}
          >
            {billingType === 'per_contact' ? 'Contact' : 'Call'} Credits:{' '}
            {activeBalance.toLocaleString()}
          </div>
        )}
        {showCredits && !isImpersonating && onBuyCreditsClick && (
          <button
            onClick={onBuyCreditsClick}
            className="btn btn-primary"
            style={{ padding: '6px 14px', fontSize: '0.8rem' }}
          >
            Buy Credits
          </button>
        )}
        {!isImpersonating && (
          <button onClick={signOut} className="btn btn-secondary">
            Sign Out
          </button>
        )}
      </div>
    </header>
  );
}
